import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import Quill from "quill";
import "quill/dist/quill.snow.css";

/** Full editorial toolbar — images / media still stripped on paste (see matchers). */
const RICH_TOOLBAR = [
  [{ header: [1, 2, 3, 4, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ script: "sub" }, { script: "super" }],
  [
    { list: "ordered" },
    { list: "bullet" },
    { indent: "-1" },
    { indent: "+1" },
  ],
  [{ align: [] }],
  ["blockquote", "code-block", "link"],
  ["clean"],
];

const RICH_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "link",
];

const BLOCKED_TAGS = new Set([
  "IMG",
  "PICTURE",
  "IFRAME",
  "VIDEO",
  "SOURCE",
  "OBJECT",
  "EMBED",
  "SVG",
]);

export function buildArticlePreviewDoc(html) {
  const body = html == null ? "" : String(html);
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 20px 22px 28px; background: #080808; color: #e2e8f0; font-family: Georgia, 'Times New Roman', ui-serif, serif; font-size: 16px; line-height: 1.7; }
  h1,h2,h3,h4 { color: #fafafa; font-family: system-ui, Segoe UI, Roboto, sans-serif; font-weight: 800; margin: 1.15em 0 0.45em; line-height: 1.2; letter-spacing: -0.02em; }
  h1 { font-size: 1.65rem; } h2 { font-size: 1.35rem; } h3 { font-size: 1.12rem; } h4 { font-size: 1rem; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; }
  p { margin: 0 0 1em; }
  a { color: #ccff00; text-underline-offset: 3px; }
  blockquote { border-left: 3px solid rgba(204,255,0,0.65); margin: 1.1em 0; padding: 0.25em 0 0.25em 1.1em; color: #94a3b8; font-style: italic; }
  pre, code { font-family: ui-monospace, 'Cascadia Code', monospace; font-size: 13px; }
  pre { margin: 1em 0; padding: 14px 16px; background: #0f0f0f; border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; overflow: auto; line-height: 1.5; color: #cbd5e1; }
  code { padding: 0.12em 0.35em; border-radius: 4px; background: rgba(255,255,255,0.06); }
  ul, ol { margin: 0 0 1em; padding-left: 1.35em; }
  li { margin: 0.25em 0; }
  img, picture, iframe, video, object, embed, svg { display: none !important; content-visibility: hidden; }
  sup, sub { font-size: 0.75em; }
</style></head><body>${body}</body></html>`;
}

/**
 * Full-feature Quill body editor + responsive live HTML preview.
 * @param {{ value: string, onChange: (v: string) => void, readOnly?: boolean, disabled?: boolean, placeholder?: string, helperText?: React.ReactNode, showPreview?: boolean, size?: 'default' | 'large' }} props
 */
export default function AdminRichEditor({
  value,
  onChange,
  readOnly = false,
  disabled = false,
  placeholder = "Compose…",
  helperText,
  showPreview = true,
  size = "large",
}) {
  const quillRef = useRef(null);
  const [tab, setTab] = useState("write");
  const frozen = readOnly || disabled;

  const modules = useMemo(
    () => ({
      toolbar: frozen ? false : RICH_TOOLBAR,
      clipboard: { matchVisual: false },
    }),
    [frozen],
  );

  useEffect(() => {
    if (frozen) return undefined;
    const t = window.setTimeout(() => {
      const ed = quillRef.current?.getEditor?.();
      if (!ed) return;
      const Delta = Quill.import("delta");
      ed.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        const tag = node?.tagName;
        if (tag && BLOCKED_TAGS.has(tag)) return new Delta();
        if (tag === "SPAN") {
          const bg = node.style?.backgroundImage;
          if (bg && /url\(/i.test(String(bg))) return new Delta();
        }
        return delta;
      });
    }, 0);
    return () => window.clearTimeout(t);
  }, [frozen]);

  const previewSrcDoc = useMemo(() => buildArticlePreviewDoc(value), [value]);

  const wrapClass = [
    "pitch-admin-quill pitch-admin-quill-rich overflow-hidden rounded-xl border border-white/[0.12] shadow-inner shadow-black/40",
    size === "large" ? "pitch-admin-quill--xl" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const previewFrame = (
    <div className="flex h-full min-h-[280px] flex-col lg:min-h-[min(520px,55vh)]">
      <div className="mb-2 flex items-center justify-between border-b border-white/[0.06] pb-2">
        <span className="font-mono-data text-[9px] font-bold uppercase tracking-[0.28em] text-slate-500">
          Live preview
        </span>
        <span className="text-[9px] text-slate-600">Sandboxed · no scripts</span>
      </div>
      <iframe
        title="Article preview"
        className="pitch-admin-html-preview w-full flex-1 rounded-lg border border-white/[0.08] bg-black"
        sandbox=""
        srcDoc={previewSrcDoc}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      {showPreview && (
        <div className="flex gap-2 lg:hidden">
          <button
            type="button"
            className={`rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
              tab === "write"
                ? "bg-[#CCFF00] text-black"
                : "border border-white/10 bg-black/40 text-slate-500 hover:text-white"
            }`}
            onClick={() => setTab("write")}
          >
            Compose
          </button>
          <button
            type="button"
            className={`rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
              tab === "preview"
                ? "bg-[#CCFF00] text-black"
                : "border border-white/10 bg-black/40 text-slate-500 hover:text-white"
            }`}
            onClick={() => setTab("preview")}
          >
            Preview
          </button>
        </div>
      )}

      <div
        className={`grid gap-5 ${showPreview ? "lg:grid-cols-2 lg:items-start" : ""}`}
      >
        <div
          className={
            showPreview
              ? `${tab === "write" ? "block" : "hidden"} min-w-0 lg:block`
              : "min-w-0"
          }
        >
          <div className={wrapClass}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value}
              onChange={onChange}
              readOnly={frozen}
              modules={modules}
              formats={RICH_FORMATS}
              placeholder={placeholder}
            />
          </div>
        </div>

        {showPreview && (
          <div
            className={`${tab === "preview" ? "block" : "hidden"} min-w-0 lg:sticky lg:top-24 lg:block`}
          >
            {previewFrame}
          </div>
        )}
      </div>

      {helperText ? (
        <p className="text-[11px] leading-relaxed text-slate-500">{helperText}</p>
      ) : null}
    </div>
  );
}

/** Read-only rendered HTML for reference panes (e.g. split-view). */
export function AdminHtmlPreviewFrame({ html, label = "Rendered" }) {
  const srcDoc = useMemo(() => buildArticlePreviewDoc(html), [html]);
  return (
    <div className="flex min-h-[200px] flex-col">
      <div className="mb-2 font-mono-data text-[9px] font-bold uppercase tracking-[0.28em] text-slate-500">
        {label}
      </div>
      <iframe
        title={label}
        className="pitch-admin-html-preview min-h-[180px] w-full flex-1 rounded-lg border border-white/[0.08] bg-black"
        sandbox=""
        srcDoc={srcDoc}
      />
    </div>
  );
}